import React, { useState } from 'react'
import { Dialog, DialogContent, Box, Typography, IconButton, Stack, Divider } from '@mui/material'

import { toast } from 'react-toastify'
import { AppDispatch } from '@src/store/store'
import { useDispatch } from 'react-redux'
import { updateAccountInfo, login, fecthNonce } from '@src/store/slices/account'
import { encodeBase64 } from 'tweetnacl-util'
import { useTranslation } from 'react-i18next'
import { getReferralCode, setReferralCode } from '@src/utils/jwt'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { googleLogin } from '@src/service/auth'
import { AccountInfo } from '@src/types/account'
import Iconify from '../iconify/iconify'
import EmailLogin from './email-login'
import GoogleLoginButton from './google-login'
import ResetPasswordConfirmation from './reset-password-confirmation'
import TwitterLogin from './twitter-login'
import TelegramLogin from './telegram-login'

interface LoginDialogProps {
  showLogin: {
    visible: boolean
    close: () => void
  }

  targetPath: string
}
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || ''
const LoginDialog: React.FC<LoginDialogProps> = ({ showLogin, targetPath }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [showResetInformation, setShowResetInformation] = useState(false)
  const [email, setEmail] = useState('')
  const referralCode = getReferralCode()
  const loginSuccess = (data: AccountInfo) => {
    toast.success(t('login.success'))
    dispatch(updateAccountInfo(data))
    showLogin.close()
    setReferralCode(null)
  }
  const connectPhantomWallet = async () => {
    try {
      setLoading(true)
      if (window.solana && !window.solana.isPhantom) {
        toast.error(t('login.phantomWalletError'))
        return
      }

      const resp = await window.solana.connect()
      const wallet = resp.publicKey.toString()

      const nonce = await dispatch(fecthNonce(wallet)).unwrap()
      const message = `By signing, you are proving you own this wallet and logging in iclub. Nonce: ${nonce}`
      const encodedMessage = new TextEncoder().encode(message)
      const signedMessage = await window.solana.signMessage(encodedMessage, 'utf8')
      const signature = encodeBase64(signedMessage.signature)

      const { code, data } = await dispatch(
        login(referralCode ? { wallet, signature, referralCode } : { wallet, signature }),
      ).unwrap()
      if (code === 0) {
        loginSuccess(data)
      }
    } catch (err) {
      toast.error(t('login.phantomWalletConnectionFailed'), {
        autoClose: false,
      })
      console.error('Failed to connect wallet:', err)
    } finally {
      setLoading(false)
    }
  }

  const onGoogleLoginSuccess = async (loginCode: string) => {
    if (loginCode) {
      setGoogleLoading(true)
      try {
        const { code, data } = await googleLogin(
          referralCode ? { code: loginCode, referralCode } : { code: loginCode },
        )
        if (code === 0) {
          loginSuccess(data)
        }
      } catch (error) {
        console.error('Google login failed:', error)
        toast.error(t('login.googleLoginFailed'), {
          autoClose: false,
        })
      } finally {
        setGoogleLoading(false)
      }
    }
  }
  const resetPasswordSucc = (email: string) => {
    setShowResetInformation(true)
    setEmail(email)
  }

  return (
    <Dialog
      title=''
      open={showLogin.visible}
      fullWidth
      maxWidth='sm'
      sx={{ maxWidth: 420, m: 'auto' }}
    >
      <DialogContent sx={{ px: 4, py: 3, position: 'relative' }}>
        <IconButton
          onClick={showLogin.close}
          sx={{ position: 'absolute', right: '10px', top: '10px' }}
        >
          <Iconify sx={{ width: 20, height: 20 }} icon='ic:outline-close' />
        </IconButton>
        {showResetInformation ? (
          <ResetPasswordConfirmation
            email={email}
            setShowResetInformation={setShowResetInformation}
          />
        ) : (
          <Box>
            <Typography variant='h4' sx={{ mb: 3 }} textAlign='center'>
              {t('login.dialog_title')}
            </Typography>
            <EmailLogin loginSuccess={loginSuccess} resetPasswordSucc={resetPasswordSucc} />
            <Divider sx={{ my: 2, color: 'text.disabled' }}>or</Divider>
            <Stack direction='row' spacing={2} alignItems='center' justifyContent='center'>
              <TwitterLogin loginSuccess={loginSuccess} />
              <TelegramLogin loginSuccess={loginSuccess} />

              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <GoogleLoginButton
                  onGoogleLoginSuccess={onGoogleLoginSuccess}
                  googleLoading={googleLoading}
                />
              </GoogleOAuthProvider>
              <IconButton
                disabled={loading}
                onClick={connectPhantomWallet}
                sx={{ border: '1px solid #e8ecee', width: 48, height: 48 }}
              >
                <Iconify sx={{ width: 32, height: 32 }} icon='token-branded:phantom' />
              </IconButton>
            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default LoginDialog
